import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PermanentJobsService } from '../services';

/**
 * React Query mutation hook for toggling candidate shortlist status
 *
 * @param {Object} [options] - Hook options
 * @param {(variables: import('../types/ShortlistCandidate.parameters').IToggleShortlistCandidatePayload) => void} [options.onSuccess] - Success callback
 * @param {(error: Error) => void} [options.onError] - Error callback
 */
export const useToggleShortlistCandidate = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError } = options;

  const mutation = useMutation({
    mutationFn: async ({ candidateId, isShortlisted }) => {
      if (isShortlisted) {
        await PermanentJobsService.removeShortlistCandidate({ candidateId });
      } else {
        await PermanentJobsService.shortlistCandidate({ candidateId });
      }
    },
    onMutate: async ({ candidateId, isShortlisted }) => {
      await queryClient.cancelQueries({ queryKey: ['GET_CANDIDATES'] });

      const previousData = queryClient.getQueriesData({
        queryKey: ['GET_CANDIDATES'],
      });

      const newIsShortlistedValue = !isShortlisted;

      queryClient.setQueriesData({ queryKey: ['GET_CANDIDATES'] }, (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          data: oldData.data.map((candidate) =>
            candidate.id === candidateId
              ? { ...candidate, isShortlisted: newIsShortlistedValue }
              : candidate,
          ),
        };
      });

      return { previousData };
    },
    onSuccess: (_, variables) => {
      if (onSuccess) {
        onSuccess(variables);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return mutation;
};
